/*
Copyright (C) 2017  Cloudbase Solutions SRL
This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.
This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
GNU Affero General Public License for more details.
You should have received a copy of the GNU Affero General Public License
along with this program.  If not, see <http://www.gnu.org/licenses/>.
*/

import React from "react";
import { Link } from "react-router";
import { observer } from "mobx-react";
import styled, { css } from "styled-components";
import autobind from "autobind-decorator";
import { ArrowRightLeft, Bell, Rocket } from "lucide-react";

import { ThemePalette, ThemeProps } from "@src/components/Theme";
import type { NotificationItemData } from "@src/@types/NotificationItem";
import StatusIcon from "@src/components/ui/StatusComponents/StatusIcon";
import IconTile from "@src/components/ui/IconTile";

/*
 * The top bar's notifications, as in the Ops Console (notif-bell / notif-dropdown): a Lucide bell
 * icon button with a dot for unseen items and a ring while something is running; the panel lists
 * the latest transfers and deployments, each opening its page.
 */
const Wrapper = styled.div<any>`
  position: relative;
`;
const Icon = styled.button<{ open?: boolean }>`
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: ${props => (props.open ? ThemePalette.grayscale[1] : "transparent")};
  color: ${props => (props.open ? ThemePalette.black : ThemePalette.grayscale[5])};
  cursor: pointer;
  &:hover {
    background: ${ThemePalette.grayscale[1]};
    color: ${ThemePalette.black};
  }
  &:focus-visible {
    outline: 2px solid ${ThemePalette.accent};
    outline-offset: 2px;
  }
`;
const BellIcon = styled.span`
  display: flex;
`;
const bellBadgePostion = css`
  position: absolute;
  top: 4px;
  right: 4px;
  border: 2px solid var(--t9-bg-surface);
  background: ${ThemePalette.alert};
`;
const Badge = styled.div<any>`
  background: ${ThemePalette.primary};
  border-radius: 50%;
  width: 8px;
  min-width: 8px;
  height: 8px;
  box-sizing: border-box;
  ${props => (props.isBellBadge ? bellBadgePostion : "")}
`;
const List = styled.div<any>`
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 360px;
  max-width: calc(100vw - 32px);
  z-index: 150;
  background: var(--t9-bg-surface);
  border: 1px solid ${ThemePalette.grayscale[2]};
  border-radius: 8px;
  box-shadow:
    0 10px 15px -3px rgb(0 0 0 / 0.1),
    0 4px 6px -4px rgb(0 0 0 / 0.1);
  overflow: hidden;
  cursor: default;
`;
const ListHead = styled.div`
  padding: 10px 14px;
  font-size: 14px;
  font-weight: 600;
  color: ${ThemePalette.grayscale[6]};
  border-bottom: 1px solid ${ThemePalette.grayscale[0]};
`;
const Rows = styled.div`
  max-height: 60vh;
  overflow-y: auto;
  padding: 4px 0;
`;
const ListItemCss = css`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 14px;
  text-decoration: none;
  color: ${ThemePalette.black};
`;
const ListItemNoLink = styled.div<any>`
  ${ListItemCss}
  cursor: default;
`;
const ListItem = styled(Link)`
  ${ListItemCss}
  cursor: pointer;
  &:hover,
  &:focus-visible {
    outline: none;
    background: ${ThemePalette.grayscale[7]};
  }
`;
const RowText = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;
const RowTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  min-width: 0;
`;
const RowTitleText = styled.span`
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
const RowDescription = styled.div`
  font-size: 12px;
  line-height: 16px;
  color: ${ThemePalette.grayscale[5]};
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;
export const InfoColumn = styled.div<any>`
  display: flex;
  flex-direction: column;
`;
export const BadgeColumn = styled.div<any>`
  display: flex;
  align-items: center;
  margin: 0 8px;
`;
export const MainItemInfo = styled.div<any>`
  display: flex;
  align-items: center;
  margin-right: -8px;

  & > div {
    margin-right: 8px;
  }
`;
export const ItemTransferBadge = styled.div<any>`
  background: "var(--t9-bg-surface)";
  color: var(--t9-text-secondary);
  font-size: 9px;
  ${ThemeProps.exactWidth("13px")}
  ${ThemeProps.exactHeight("10px")}
  display: flex;
  justify-content: center;
  align-items: center;
  font-weight: 500;
  border-radius: 2px;
  border: 1px solid var(--t9-text-secondary);
`;
export const ItemTitle = styled.div<any>`
  ${props =>
    props.nowrap ? "white-space: nowrap;" : "word-break: break-word;"}
  overflow: hidden;
  text-overflow: ellipsis;
`;
export const ItemDescription = styled.div<any>`
  color: ${ThemePalette.grayscale[5]};
  font-size: 10px;
  margin-top: 8px;
`;
const NoItems = styled.div<any>`
  width: 100%;
  padding: 12px 0;
  text-align: center;
  font-size: 13px;
  color: ${ThemePalette.grayscale[5]};
`;
// Something is running: a small pulsing cyan dot at the bell's foot.
const Loading = styled.span`
  position: absolute;
  bottom: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  box-sizing: border-box;
  border-radius: 50%;
  border: 2px solid var(--t9-bg-surface);
  background: ${ThemePalette.accent};
  pointer-events: none;
  ${ThemeProps.animations.disabledLoading}
`;

export type Props = {
  white?: boolean;
  items: NotificationItemData[];
  onClose: () => void;
};
type State = {
  showDropdownList: boolean;
};
@observer
class NotificationDropdown extends React.Component<Props, State> {
  state = {
    showDropdownList: false,
  };

  itemMouseDown: boolean | undefined;

  buttonRef = React.createRef<HTMLButtonElement>();

  keepOpen = {
    onMouseDown: () => {
      this.itemMouseDown = true;
    },
    onMouseUp: () => {
      this.itemMouseDown = false;
    },
  };

  componentDidMount() {
    window.addEventListener("mousedown", this.handlePageClick, false);
    window.addEventListener("keydown", this.handleKeyDown, false);
  }

  componentWillUnmount() {
    window.removeEventListener("mousedown", this.handlePageClick, false);
    window.removeEventListener("keydown", this.handleKeyDown, false);
  }

  handleItemClick() {
    this.setState({ showDropdownList: false });
    this.props.onClose();
  }

  @autobind
  handlePageClick() {
    if (!this.itemMouseDown) {
      if (this.state.showDropdownList) {
        this.props.onClose();
      }
      this.setState({ showDropdownList: false });
    }
  }

  @autobind
  handleKeyDown(e: KeyboardEvent) {
    if (e.key === "Escape" && this.state.showDropdownList) {
      this.props.onClose();
      this.setState({ showDropdownList: false });
      this.buttonRef.current?.focus();
    }
  }

  handleButtonClick() {
    if (this.state.showDropdownList) {
      this.props.onClose();
    }

    this.setState(prevState => ({
      showDropdownList: !prevState.showDropdownList,
    }));
  }

  renderNoItems() {
    if (
      !this.state.showDropdownList ||
      (this.props.items && this.props.items.length > 0)
    ) {
      return null;
    }

    return (
      <List id="swift-notifications" {...this.keepOpen}>
        <ListHead>Notifications</ListHead>
        <ListItemNoLink>
          <NoItems>There are no notifications</NoItems>
        </ListItemNoLink>
      </List>
    );
  }

  renderList() {
    if (
      !this.state.showDropdownList ||
      !this.props.items ||
      this.props.items.length === 0
    ) {
      return null;
    }

    return (
      <List id="swift-notifications" {...this.keepOpen}>
        <ListHead>Notifications</ListHead>
        <Rows>
          {this.props.items.map(item => {
            const typeUrl =
              item.type === "deployment" ? "deployments" : "transfers";

            const executionsPath =
              item.status === "RUNNING"
                ? item.type === "transfer"
                  ? "/executions"
                  : item.type === "deployment"
                    ? "/tasks"
                    : ""
                : "";
            const isDeployment = item.type === "deployment";

            return (
              <ListItem
                key={item.id}
                onClick={() => {
                  this.handleItemClick();
                }}
                to={`/${typeUrl}/${item.id}${executionsPath}`}
              >
                <IconTile
                  icon={isDeployment ? Rocket : ArrowRightLeft}
                  tone={isDeployment ? "brand" : "info"}
                  size={28}
                  title={isDeployment ? "Deployment" : "Transfer"}
                />
                <RowText>
                  <RowTitle>
                    <StatusIcon status={item.status} hollow />
                    <RowTitleText>{item.name}</RowTitleText>
                  </RowTitle>
                  <RowDescription>{item.description}</RowDescription>
                </RowText>
                {item.unseen ? <Badge aria-label="New" /> : null}
              </ListItem>
            );
          })}
        </Rows>
      </List>
    );
  }

  renderBell() {
    const isLoading = Boolean(
      this.props.items.find(i => i.status === "RUNNING"),
    );
    const unseen = this.props.items.filter(i => i.unseen).length;
    const open = this.state.showDropdownList;

    return (
      <Icon
        ref={this.buttonRef}
        type="button"
        open={open}
        aria-label={unseen ? `Notifications, ${unseen} new` : "Notifications"}
        title="Notifications"
        aria-haspopup="true"
        aria-expanded={open}
        aria-controls={open ? "swift-notifications" : undefined}
        {...this.keepOpen}
        onClick={() => this.handleButtonClick()}
      >
        <BellIcon>
          <Bell size={18} strokeWidth={1.75} aria-hidden="true" />
        </BellIcon>
        {unseen ? <Badge isBellBadge /> : null}
        {isLoading ? <Loading aria-hidden="true" title="Running" /> : null}
      </Icon>
    );
  }

  render() {
    return (
      <Wrapper>
        {this.renderBell()}
        {this.renderList()}
        {this.renderNoItems()}
      </Wrapper>
    );
  }
}

export default NotificationDropdown;
